/**
 * Handles manual popup triggers from the custom popup section.
 * This script only opens and closes popups that are set to 'Link Trigger'.
 * It is designed to work alongside the theme's main JavaScript and replicates
 * the theme's animation logic for popup content.
 */
document.addEventListener('DOMContentLoaded', () => {
  const popupSection = document.querySelector('[data-section-type="popup"]');

  // If the popup section doesn't exist on the current page, do nothing.
  if (!popupSection) {
    return;
  }

  // --- Animation Constants (based on theme.js) ---
  const ANIMATION_ITEM_SELECTOR = ".animation--popup-item";
  const REVEALED_CLASS = "animation--popup-revealed";
  const VISIBLE_CLASS = "visible";
  const STAGGER_DELAY_MS = 100; // Stagger delay between each item's animation

  let activePopup = null;

  /**
   * Checks if animations should run based on user's motion preferences.
   * @returns {boolean}
   */
  const shouldAnimate = () => {
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  /**
   * Applies a staggered transition delay to animation items within a popup.
   * This replicates the `delayOffset` function from the theme.
   * @param {HTMLElement} popup - The popup element.
   */
  const applyAnimationStagger = (popup) => {
    const items = popup.querySelectorAll(ANIMATION_ITEM_SELECTOR);
    items.forEach((item, index) => {
      item.style.transitionDelay = `${index * STAGGER_DELAY_MS}ms`;
    });
  };

  /**
   * Opens a popup, lazy-loads its content on first open, and triggers animations.
   * @param {HTMLElement} popup - The popup element to open.
   */
  const openPopup = (popup) => {
    if (!popup || popup.classList.contains(VISIBLE_CLASS)) return;

    // --- NEW: LAZY-LOADING LOGIC ---
    // Check if the content has already been loaded for this popup
    if (!popup.dataset.contentLoaded) {
      const template = popup.querySelector('.popup-content-template');
      const targetContainer = popup.querySelector('.popup__text-container');

      if (template && targetContainer) {
        // Clone the content from the template and append it to the target
        const content = template.content.cloneNode(true);
        targetContainer.appendChild(content);

        // Mark this popup as loaded so we don't do it again
        popup.dataset.contentLoaded = 'true';
        
        // Sometimes, Elfsight's platform script is already on the page and
        // won't automatically detect a new widget. This line forces it to re-scan.
        if (window.Es && window.Es.platform) {
            window.Es.platform.init();
        }
      }
    }
    // --- END OF NEW LOGIC ---

    activePopup = popup;

    // Make the popup visible first
    popup.classList.add(VISIBLE_CLASS);
    document.body.classList.add('popup-is-open');

    // Animate the content if animations are enabled
    if (shouldAnimate()) {
      applyAnimationStagger(popup);
      // A tiny timeout ensures the browser has applied the 'visible' class
      // before adding the animation class, allowing the transition to fire.
      setTimeout(() => {
        popup.classList.add(REVEALED_CLASS);
      }, 10);
    }

    // Add listeners for closing this specific popup
    document.addEventListener('keydown', handleEscKey);
    popup.addEventListener('click', handleCloseClick);
  };

  /**
   * Closes the currently active popup and resets its animations.
   */
  const closePopup = () => {
    if (!activePopup) return;
    const popupToClose = activePopup;
    activePopup = null; // Unset immediately to prevent race conditions

    // Remove animation class to allow content to animate out
    if (shouldAnimate()) {
      popupToClose.classList.remove(REVEALED_CLASS);
    }

    // Hide the popup after its main transition (e.g., fade out) is complete.
    // We use a timeout as a reliable fallback in case the event doesn't fire.
    const hidePopup = () => {
      popupToClose.classList.remove(VISIBLE_CLASS);
      document.body.classList.remove('popup-is-open');
      popupToClose.removeEventListener('transitionend', onTransitionEnd);
    };

    let fallbackTimeout = setTimeout(hidePopup, 500); // Fallback for safety

    const onTransitionEnd = (e) => {
      // Ensure we're listening to the popup's own transition, not a child's.
      if (e.target === popupToClose) {
        clearTimeout(fallbackTimeout);
        hidePopup();
      }
    };

    popupToClose.addEventListener('transitionend', onTransitionEnd);

    // If not animating, hide immediately
    if (!shouldAnimate()) {
        hidePopup();
    }

    // Clean up global listeners
    document.removeEventListener('keydown', handleEscKey);
  };

  /**
   * Handles the Escape key press to close the popup.
   * @param {KeyboardEvent} event
   */
  const handleEscKey = (event) => {
    if (event.key === 'Escape') {
      closePopup();
    }
  };

  /**
   * Handles clicks for closing the popup (on dismiss buttons or the background wash).
   * @param {MouseEvent} event
   */
  const handleCloseClick = (event) => {
    if (event.target.closest('[data-dismiss-popup]') || event.target.matches('.popup__wash')) {
      closePopup();
    }
  };

  // Global listener for all clicks on the page to detect manual triggers.
  document.body.addEventListener('click', (event) => {
    const triggerLink = event.target.closest('a');

    if (!triggerLink || !triggerLink.hash) {
      return;
    }

    try {
      const popup = popupSection.querySelector(triggerLink.hash);

      // If a matching popup exists and is set to 'link_trigger', open it.
      if (popup && popup.dataset.trigger === 'link_trigger') {
        event.preventDefault(); // Prevent the browser from jumping to the anchor
        openPopup(popup);
      }
    } catch (e) {
      // The hash is not a valid selector, so we ignore it.
      console.warn(`[Popup Section] Could not query selector for hash: ${triggerLink.hash}`);
    }
  });
});